import { useState } from 'react';
import { Copy, Download, Shield, X, Check } from 'lucide-react';

/**
 * One-time recovery code modal. Shown once at signup (and after the user
 * regenerates from Profile). Caller passes onClose so the user must explicitly
 * confirm they saved it before continuing.
 */
export default function RecoveryCodeModal({ code, email, onClose, title, intro }) {
    const [copied, setCopied] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };

    const download = () => {
        const lines = [
            'Quiet Mind — Recovery Code',
            '',
            `Email: ${email || ''}`,
            `Recovery code: ${code}`,
            '',
            'Keep this code somewhere safe. It is the only way to reset your',
            'password if you forget it. Each time you use it, a new code will',
            'be issued to replace this one.',
        ].join('\n');
        const blob = new Blob([lines], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'quiet-mind-recovery-code.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-forest/30 backdrop-blur-sm grid place-items-center px-4 animate-fade-in"
            role="dialog"
            aria-modal="true"
            data-testid="recovery-code-modal"
        >
            <div className="qm-card max-w-md w-full p-7 md:p-8 relative">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    data-testid="recovery-modal-close"
                    className="absolute top-3 right-3 rounded-full w-9 h-9 grid place-items-center text-forest-soft hover:bg-dusty/40 hover:text-forest"
                >
                    <X size={16} />
                </button>

                <div className="w-12 h-12 rounded-2xl bg-sage/40 grid place-items-center mb-4">
                    <Shield size={22} className="text-forest" />
                </div>

                <h2 className="font-serif text-2xl text-forest" data-testid="recovery-modal-title">
                    {title || 'Save your recovery code'}
                </h2>
                <p className="text-sm text-forest-soft mt-1.5">
                    {intro ||
                        'This is the only way to reset your password if you forget it. We won’t show it again — please save it somewhere safe.'}
                </p>

                <div
                    className="mt-5 rounded-2xl bg-cream-deep/60 border border-forest/10 px-4 py-3 font-mono tracking-widest text-forest text-center text-sm md:text-base select-all break-all"
                    data-testid="recovery-code-value"
                >
                    {code}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                    <button
                        type="button"
                        onClick={copy}
                        data-testid="recovery-copy-button"
                        className="qm-btn-ghost inline-flex items-center justify-center gap-2 text-sm"
                    >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                        {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button
                        type="button"
                        onClick={download}
                        data-testid="recovery-download-button"
                        className="qm-btn-ghost inline-flex items-center justify-center gap-2 text-sm"
                    >
                        <Download size={14} /> Download .txt
                    </button>
                </div>

                <label className="flex items-start gap-2 mt-5 text-sm text-forest-soft cursor-pointer">
                    <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        data-testid="recovery-confirmed-checkbox"
                        className="mt-1"
                    />
                    <span>I’ve saved my recovery code somewhere safe.</span>
                </label>

                <button
                    type="button"
                    onClick={onClose}
                    disabled={!confirmed}
                    data-testid="recovery-modal-continue"
                    className="qm-btn-primary w-full mt-4 disabled:opacity-60"
                >
                    Continue
                </button>
            </div>
        </div>
    );
}
